import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { config } from '../../config/environment';
import styles from './GameChat.module.css';
import BotsInterview from './BotsInterview.jsx';
import SignatureChat from './SignatureChat.jsx';
import GameAssistantChat from './GameAssistantChat.jsx';
import { fetchCanvassStatus } from '../../utils/canvasses';
import { logger } from '../../utils/logger';

const GameChat = () => {
  const { id } = useParams();
  const [pills, setPills] = useState(null);
  const [introductionData, setIntroductionData] = useState(null);
  const [interviewId, setInterviewId] = useState(null);
  const [shouldTerminate, setShouldTerminate] = useState(false);
  const [canvassData, setCanvassData] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [statusError, setStatusError] = useState(null);
  const hasFilteredFirstMessage = useRef(false);

  // Fetch the canvass status so we can choose the configured interview experience
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!id) {
        setLoadingStatus(false);
        return;
      }
      setLoadingStatus(true);
      setStatusError(null);
      try {
        const status = await fetchCanvassStatus({ canvassId: id });
        if (!cancelled) setCanvassData(status);
      } catch (e) {
        if (!cancelled) setStatusError(e?.message || 'Failed to load interview status');
      } finally {
        if (!cancelled) setLoadingStatus(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  // Use the game-specific API endpoint with game ID in path and environment parameters
  const runtimeUrl = React.useMemo(() => {
    let baseUrl;
    // For local development, use localhost:7071, for production use configured endpoint
    if (config.environment === 'development' || window.location.hostname === 'localhost') {
      baseUrl = `http://localhost:7071/api/chat/${id}`;
    } else {
      // For production/staging, the interviewApiBase already includes /api/interview
      // So we just need to replace the endpoint and add the ID
      baseUrl = config.interviewApiBase.replace('/api/interview', `/api/chat/${id}`);
    }
    
    const url = new URL(baseUrl);
    url.searchParams.append('environment', config.environment);
    url.searchParams.append('frontend_url', config.frontendUrl);
    return url.toString();
  }, [id, config.environment, config.frontendUrl]);                            

  // Helper: apply disabled state to CopilotKit chat input and controls when shouldTerminate is true
  const applyInputDisabledState = React.useCallback(() => {
    try {
      const inputContainer = document.querySelector('.copilotKitInputContainer') || document.querySelector('.copilotKitInput');
      if (!inputContainer) return;

      const textarea = inputContainer.querySelector('textarea');
      const submitBtn = inputContainer.querySelector('button[type="submit"]');

      if (textarea) {
        textarea.disabled = shouldTerminate;
        if (shouldTerminate) {
          textarea.value = '';
          textarea.setAttribute('placeholder', 'Thank you so much for your time and insights.');
        } else {
          textarea.removeAttribute('placeholder');
        }
      }
      if (submitBtn) {
        submitBtn.disabled = shouldTerminate;
        submitBtn.style.opacity = shouldTerminate ? '0' : '';
      }

      // Visually dim the input container when disabled
      inputContainer.style.opacity = shouldTerminate ? '0.4' : '';
      inputContainer.style.pointerEvents = shouldTerminate ? 'none' : '';
    } catch (e) {
      // no-op
      logger.error('applyInputDisabledState: Error applying disabled state', e);
    }
  }, [shouldTerminate]);

  // Prevent submitting in the CopilotKit input when shouldTerminate is true
  useEffect(() => {
    const isCopilotElement = (el) => {
      if (!el) return false;
      return el.closest('.copilotKitInputContainer') || el.closest('.copilotKitInput');
    };

    const onKeyDown = (e) => {
      if (!shouldTerminate) return;
      if (e.key === 'Enter' && !e.shiftKey && isCopilotElement(e.target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const onSubmit = (e) => {
      if (!shouldTerminate) return;
      if (!isCopilotElement(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('submit', onSubmit, true);

    // Apply disabled state to current DOM
    applyInputDisabledState();

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('submit', onSubmit, true);
      // Re-enable controls on cleanup if needed
      if (!shouldTerminate) applyInputDisabledState();
    };
  }, [shouldTerminate, applyInputDisabledState]);

  // Dynamic input container height adjustment
  useEffect(() => {
    const adjustMessagesHeight = () => {
      const inputContainer = document.querySelector('.copilotKitInputContainer');
      const messagesContainer = document.querySelector('.copilotKitMessages');

      if (inputContainer && messagesContainer) {
        const inputHeight = inputContainer.offsetHeight;
        messagesContainer.style.bottom = `${inputHeight}px`;
      }
    };

    // Initial adjustment
    setTimeout(adjustMessagesHeight, 100);

    // Create observer for input container size changes
    const resizeObserver = new ResizeObserver(adjustMessagesHeight);
    const inputContainer = document.querySelector('.copilotKitInputContainer');
    if (inputContainer) {
      resizeObserver.observe(inputContainer);
    }

    // Also observe DOM changes to catch when input container is added
    const mutationObserver = new MutationObserver(() => {
      const inputContainer = document.querySelector('.copilotKitInputContainer');
      if (inputContainer && !inputContainer.dataset.observed) {
        inputContainer.dataset.observed = 'true';
        resizeObserver.observe(inputContainer);
        adjustMessagesHeight();
        // If interview is terminated, enforce disabled state once input appears
        if (shouldTerminate) {
          applyInputDisabledState();
        }
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [shouldTerminate, applyInputDisabledState]);

  // Remove CopilotKit message controls entirely if they appear
  useEffect(() => {
    const removeControls = (root = document) => {
      try {
        const nodes = root.querySelectorAll?.('.copilotKitMessageControls');
        if (nodes && nodes.length) {
          nodes.forEach((el) => el.remove());
        }
      } catch {}
    };

    // Remove any existing controls now
    removeControls();

    // Observe future DOM changes to remove newly added controls
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            const el = node;
            if (el.classList && el.classList.contains('copilotKitMessageControls')) {
              el.remove();
              return;
            }
            if (el.querySelectorAll) {
              el.querySelectorAll('.copilotKitMessageControls').forEach((found) => found.remove());
            }
          }
        });
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Override global fetch to capture pills from responses
  // useEffect(() => {
  //   const originalFetch = window.fetch;

  //   window.fetch = async (url, options) => {
  //     if (typeof url === 'string' && url.includes('/copilotkit')) {
  //       try {
  //         const response = await originalFetch(url, options);
  //         const clonedResponse = response.clone();
  //         const data = await clonedResponse.json();

  //         // Check if the response contains pills and introduction
  //         if (data?.data?.generateCopilotResponse?.extensions) {
  //           if ('pills' in data.data.generateCopilotResponse.extensions) {
  //             const pillsData = data.data.generateCopilotResponse.extensions.pills;
  //             setPills(pillsData);
  //           }
  //           if ('introduction' in data.data.generateCopilotResponse.extensions) {
  //             const intro = data.data.generateCopilotResponse.extensions.introduction;
  //             setIntroductionData(intro);
  //           }
  //           if ('interview_id' in data.data.generateCopilotResponse.extensions) {
  //             const intId = data.data.generateCopilotResponse.extensions.interview_id;
  //             setInterviewId(intId);
  //             logger.log('Captured interview_id from response:', intId);
  //           }
  //           if ('shouldTerminate' in data.data.generateCopilotResponse.extensions) {
  //             const term = data.data.generateCopilotResponse.extensions.shouldTerminate;
  //             setShouldTerminate(Boolean(term));
  //           }
  //         }

  //         // Filter out introduction text from messages if it appears
  //         if (data?.data?.generateCopilotResponse?.messages) {
  //           // Get introduction text from current response or stored data
  //           const currentIntro = data.data.generateCopilotResponse.extensions?.introduction?.introduction_text;
  //           const introText = currentIntro || introductionData?.introduction_text;

  //           if (introText && !hasFilteredFirstMessage.current) {
  //             data.data.generateCopilotResponse.messages = data.data.generateCopilotResponse.messages.map(msg => {
  //               if (msg.content && Array.isArray(msg.content)) {
  //                 msg.content = msg.content.map(content => {
  //                   if (typeof content === 'string' && content.includes(introText)) {
  //                     // Remove the introduction text completely
  //                     const filteredContent = content.replace(introText, '').trim();
  //                     hasFilteredFirstMessage.current = true;
  //                     return filteredContent;
  //                   }
  //                   return content;
  //                 });
  //               }
  //               return msg;
  //             });
  //           }
  //         }

  //         // Return a new response with the modified data
  //         return new Response(JSON.stringify(data), {
  //           status: response.status,
  //           statusText: response.statusText,
  //           headers: response.headers
  //         });
  //       } catch (error) {
  //         logger.error('Runtime response error:', error);
  //         return originalFetch(url, options);
  //       }
  //     }
  //     return originalFetch(url, options);
  //   };

  //   return () => {
  //     window.fetch = originalFetch;
  //   };
  // }, []);

  const renderChat = () => {
    const commonProps = {
      runtimeUrl,
      interviewId: id,
      onPills: setPills,
      onIntroduction: setIntroductionData,
      onInterviewId: (v) => setInterviewId(v),
      onShouldTerminate: (v) => setShouldTerminate(Boolean(v)),
      disabled: shouldTerminate,
      disabledPlaceholder: 'Thank you so much for your time and insights. (GameChat2)',
      introductionData,
      canvassData,
    };

    const experience = canvassData?.interviewExperience || 'BotsInterview';
    switch (experience) {
      case 'SignatureChat':
        return <SignatureChat {...commonProps} />;
      case 'GameAssistantChat':
        return <GameAssistantChat {...commonProps} />;
      case 'BotsInterview':
      default:
        return <BotsInterview {...commonProps} />;
    }
  };

  if (loadingStatus) {
    return (
      <div className={styles.gameChatContainer} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        Loading interview…
      </div>
    );
  }

  if (statusError) {
    return (
      <div className={styles.gameChatContainer} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column' }}>
        <div>Unable to load interview.</div>
        <div style={{ marginTop: 8, color: 'var(--text-secondary, #6c757d)' }}>{statusError}</div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.gameChatContainer}`}
      style={{ overflow: 'auto', alignItems: 'flex-start', justifyContent: 'flex-start' }}
    >
      {renderChat()}
    </div>
  );
};

export default GameChat;
